import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { CallReviewDetail, type ReviewCall } from "./dial-analytics/callReviewParts";
import CourseScenarioPanel from "./dial-analytics/CourseScenarioPanel";
import { courseTrackForRole } from "../utils/scenarioForCall";

// =============================================================================
// DialReviewModal — the full /dialview call-review for ONE dial
// =============================================================================
// Opened by the "Expand transcript" button on a LeadTimelinePanel dial card. The
// inline card only had a cramped flat transcript; this gives the same rich view
// the connects panel has: recording + full-height transcript (per-line m:ss +
// click-to-seek when word-times exist) beside the phone-setter course script.
//
// Single-call (no list column), so it reuses the shared CallReviewDetail +
// CourseScenarioPanel directly. z-[10000] so it sits above LeadTimelineModal
// (z-[9999]), which is where it is usually opened from.
// =============================================================================

interface DialReviewModalProps {
  call: ReviewCall | null;
  isOpen: boolean;
  timezone: string;
  onClose: () => void;
  /** When set AND the call carries a leadEmail, a footer link opens that lead's
   *  full /leadtimeline. The host owns the timeline modal (and closes this one
   *  first, since this modal sits above it). Omit to hide the link — e.g. when
   *  opened from inside a timeline already. */
  onOpenTimeline?: (email: string) => void;
  /** Open seeked to this second (Course page examples). */
  startSec?: number;
  /** Open the course panel on this lesson file, scrolled to `coursePhrase`. */
  courseFile?: string;
  coursePhrase?: string;
}

const DialReviewModal: React.FC<DialReviewModalProps> = ({
  call,
  isOpen,
  timezone,
  onClose,
  onOpenTimeline,
  startSec,
  courseFile,
  coursePhrase,
}) => {
  // Course script on by default — the whole reason to expand is to read the call
  // against the scenario. Toggle for a wider transcript-only read.
  const [showCourse, setShowCourse] = useState(true);
  // A course teleport requested by clicking a green (on-script) transcript span.
  // Tagged with the call it belongs to, so a stale jump is dropped by derivation
  // (no reset effect — the repo lint bans setState-in-effect).
  const [jumpTo, setJumpTo] = useState<{ callId: string; file: string; phrase: string } | null>(
    null
  );
  // Course page examples open the panel on the lesson. Memoized: the panel's
  // teleport effect re-runs on every new jumpTo object. Released after it has
  // landed: CourseScenarioPanel re-applies a live jumpTo whenever its file
  // changes, so a held jump would snap the viewer's picker back to the lesson.
  const lessonJump = useMemo(
    () => (courseFile ? { file: courseFile, phrase: coursePhrase ?? "" } : null),
    [courseFile, coursePhrase]
  );
  const [lessonJumpDone, setLessonJumpDone] = useState(false);
  useEffect(() => {
    if (!isOpen || !lessonJump) return;
    const t = setTimeout(() => setLessonJumpDone(true), 2000);
    return () => clearTimeout(t);
  }, [isOpen, lessonJump]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !call) return null;

  // Caller role → course track (closer course for closer dials).
  const track = courseTrackForRole(call.dialerRole);

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Call review"
        data-testid="dial-review-modal"
        className="relative detail-overlay rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col mx-4"
      >
        {/* Header — title + course toggle + close */}
        <div className="flex items-center justify-between p-4 border-b border-black/[0.08] shrink-0">
          <h2 className="token-18 text-glass">
            Call review{call.leadName ? ` · ${call.leadName}` : ""}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowCourse((v) => !v)}
              aria-pressed={showCourse}
              data-testid="dial-review-course-toggle"
              className={`anim-tap text-xs rounded-full px-3 py-1.5 border transition-colors ${
                showCourse
                  ? "border-ds-accent/40 bg-ds-accent/[0.08] text-ds-accent"
                  : "border-black/[0.12] text-glass-secondary hover:bg-black/[0.04]"
              }`}
            >
              📖 Course
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close call review"
              className="anim-tap text-lg text-glass-muted hover:text-glass px-2"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Body — detail (transcript) + course script */}
        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          <div className="flex-1 min-h-0 min-w-0">
            <CallReviewDetail
              call={call}
              timezone={timezone}
              testId="dial-review-detail"
              startSec={startSec}
              onScriptJump={(a) => setJumpTo({ callId: call.id, file: a.file, phrase: a.phrase })}
              footer={
                onOpenTimeline && call.leadEmail ? (
                  <button
                    type="button"
                    onClick={() => call.leadEmail && onOpenTimeline(call.leadEmail)}
                    data-testid="dial-review-open-timeline"
                    className="anim-tap token-11 text-ds-accent hover:underline"
                  >
                    Open full timeline →
                  </button>
                ) : undefined
              }
            />
          </div>
          {showCourse && (
            <div className="md:w-[30rem] shrink-0 min-h-0 border-t md:border-t-0 md:border-l border-black/[0.08]">
              <CourseScenarioPanel
                key={`${call.id}:${track}`}
                track={track}
                funnelAtDial={call.funnelAtDial}
                jumpTo={jumpTo?.callId === call.id ? jumpTo : lessonJumpDone ? null : lessonJump}
              />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DialReviewModal;
